/*
  Prefill Coverage Report — admin-only diagnostic surface for the
  Intake → Assessment pipeline. Answers "which intake fields have
  nowhere to land in the assessment schemas?" so a maintainer can
  decide which rows to add to ``ONBOARDING_TO_ASSESSMENT`` or which
  assessment field to add.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Shield, CheckCircle2, Zap, AlertTriangle, Loader2, Search,
  ChevronLeft, Workflow, MinusCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

function Pill({ tone, children }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky:     "bg-sky-50 text-sky-800 border-sky-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    red:     "bg-red-50 text-red-800 border-red-200",
    zinc:    "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[tone]}`}>
      {children}
    </span>
  );
}

function KpiTile({ label, value, tone = "zinc", icon: Icon }) {
  const map = {
    emerald: "bg-emerald-50/70 border-emerald-200 text-emerald-900",
    sky:     "bg-sky-50/70 border-sky-200 text-sky-900",
    amber:   "bg-amber-50/70 border-amber-200 text-amber-900",
    red:     "bg-red-50/70 border-red-200 text-red-900",
    zinc:    "bg-white border-zinc-200 text-zinc-900",
  };
  return (
    <div className={`border rounded p-3 ${map[tone]}`}>
      <div className="flex items-center gap-1 text-2xs uppercase tracking-wider opacity-75">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-2xl font-semibold font-mono-num mt-0.5">{value}</div>
    </div>
  );
}

export default function PrefillCoverage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/prefill-coverage");
      setReport(r.data);
    } catch (e) {
      toast.error("Couldn't load coverage report", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!report) return [];
    const base = [
      ...(report.bridged || []),
      ...(report.unbridged || []),
      ...(report.not_applicable || []),
    ];
    const q = search.trim().toLowerCase();
    return base.filter((r) => {
      if (filter === "unbridged" && r.bridge_type !== "none") return false;
      if (filter === "bridged" && ["none", "not_applicable"].includes(r.bridge_type)) return false;
      if (filter === "rule" && r.bridge_type !== "rule") return false;
      if (filter === "na" && r.bridge_type !== "not_applicable") return false;
      if (filter === "safety" && !r.safety_critical) return false;
      if (filter === "direct" && r.bridge_type !== "direct") return false;
      if (filter === "alias" && r.bridge_type !== "alias") return false;
      if (!q) return true;
      return (
        (r.label || "").toLowerCase().includes(q)
        || (r.canonical_key || "").toLowerCase().includes(q)
        || (r.template_name || "").toLowerCase().includes(q)
        || (r.field_id || "").toLowerCase().includes(q)
      );
    });
  }, [report, filter, search]);

  const counts = report?.counts || {};

  return (
    <div className="max-w-[1300px] mx-auto p-4 space-y-3" data-testid="prefill-coverage-page">
      <div className="flex items-center gap-2">
        <Link to="/settings" className="text-zinc-400 hover:text-zinc-700" data-testid="pfc-back">
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
          <Shield className="h-6 w-6" /> Prefill Coverage
        </h1>
      </div>
      <div className="text-xs text-zinc-500 max-w-3xl">
        Every onboarding intake field with a <code className="text-zinc-700">canonicalFieldKey</code> is listed below along with the assessment field(s) it pre-fills — directly, via an alias, or via a mapping rule (tick-boxes → choices / lists, Yes/No → Yes/No/Unsure, combined labelled text). Non-clinical answers (identity, contact, consent, billing) are marked Not applicable because they live on the client record.
      </div>

      {loading && (
        <div className="text-xs text-zinc-500 flex items-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Building coverage report…
        </div>
      )}

      {!loading && report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <KpiTile label="Total intake fields" value={counts.total_intake_canonical_fields} icon={Shield} />
            <KpiTile label="Direct bridge" value={counts.direct_bridged} tone="emerald" icon={CheckCircle2} />
            <KpiTile label="Alias bridge" value={counts.alias_bridged} tone="sky" icon={Zap} />
            <KpiTile label="Mapping rule" value={counts.rule_bridged ?? 0} tone="emerald" icon={Workflow} />
            <KpiTile label="Not applicable" value={counts.not_applicable ?? 0} icon={MinusCircle} />
            <KpiTile label="Unbridged" value={counts.unbridged}
              tone={counts.unbridged > 0 ? "amber" : "zinc"} icon={AlertTriangle} />
            <KpiTile label="Safety-critical unbridged" value={counts.safety_critical_unbridged}
              tone={counts.safety_critical_unbridged > 0 ? "red" : "zinc"} icon={AlertTriangle} />
          </div>

          {(report.unbridged_by_template || []).length > 0 && (
          <Card className="p-3 rounded-md border-amber-200 bg-amber-50/40">
            <div className="text-2xs uppercase tracking-wider text-amber-900 mb-1">
              Templates with the biggest gaps
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(report.unbridged_by_template || [])
                .sort((a, b) => b.unbridged_count - a.unbridged_count)
                .map((t) => (
                  <div key={t.template_id}
                    className="bg-white border border-amber-100 rounded px-2 py-1.5"
                    data-testid={`pfc-template-${t.template_id}`}
                  >
                    <div className="text-2xs font-medium text-zinc-900">
                      {t.template_name}
                    </div>
                    <div className="text-2xs text-zinc-500">
                      {t.unbridged_count} unbridged · {t.file}
                    </div>
                  </div>
                ))}
            </div>
          </Card>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5" data-testid="pfc-filter">
              {[
                { k: "all", label: "All" },
                { k: "unbridged", label: "Unbridged" },
                { k: "safety", label: "Safety-critical" },
                { k: "direct", label: "Direct" },
                { k: "alias", label: "Alias" },
                { k: "rule", label: "Rule" },
                { k: "na", label: "Not applicable" },
                { k: "bridged", label: "Bridged" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  className={`text-2xs px-2.5 py-1 rounded ${filter === t.k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                  data-testid={`pfc-filter-${t.k}`}
                >{t.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-1 flex-1 max-w-sm">
              <Search className="h-3.5 w-3.5 text-zinc-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by field, canonical key or template…"
                className="h-8 text-xs" data-testid="pfc-search"
              />
            </div>
            <Button variant="outline" size="sm" className="text-xs h-8" onClick={load}>
              Refresh
            </Button>
          </div>

          <div className="text-2xs text-zinc-500">Showing {rows.length} field{rows.length === 1 ? "" : "s"}</div>

          <div className="border border-zinc-200 rounded overflow-hidden">
            <table className="w-full text-xs" data-testid="pfc-table">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Template</th>
                  <th className="text-left px-2 py-1.5">Intake field</th>
                  <th className="text-left px-2 py-1.5">Canonical key</th>
                  <th className="text-left px-2 py-1.5">Bridge</th>
                  <th className="text-left px-2 py-1.5">Assessment target</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 bg-white">
                {rows.slice(0, 1000).map((r, i) => (
                  <tr key={`${r.template_id}-${r.field_id}-${i}`}
                    data-testid={`pfc-row-${r.template_id}-${r.field_id}`}
                  >
                    <td className="px-2 py-1.5 text-zinc-500 text-2xs">{r.template_name}</td>
                    <td className="px-2 py-1.5">
                      <div className="text-zinc-900">{r.label || <span className="italic text-zinc-400">(no label)</span>}</div>
                      <div className="text-2xs text-zinc-400 font-mono-num">{r.field_id}</div>
                    </td>
                    <td className="px-2 py-1.5 font-mono-num text-2xs text-zinc-700">{r.canonical_key}</td>
                    <td className="px-2 py-1.5">
                      {r.bridge_type === "direct" && <Pill tone="emerald"><CheckCircle2 className="h-2.5 w-2.5" />Direct</Pill>}
                      {r.bridge_type === "alias"  && <Pill tone="sky"><Zap className="h-2.5 w-2.5" />Alias</Pill>}
                      {r.bridge_type === "rule"   && <Pill tone="emerald"><Workflow className="h-2.5 w-2.5" />Rule</Pill>}
                      {r.bridge_type === "not_applicable" && <Pill tone="zinc"><MinusCircle className="h-2.5 w-2.5" />N/A</Pill>}
                      {r.bridge_type === "none"   && <Pill tone={r.safety_critical ? "red" : "amber"}><AlertTriangle className="h-2.5 w-2.5" />{r.safety_critical ? "Safety · unbridged" : "Unbridged"}</Pill>}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.assessment_key ? (
                        <>
                          <div className="font-mono-num text-2xs text-zinc-700">{r.assessment_key}</div>
                          <div className="text-2xs text-zinc-500">
                            {(r.assessment_targets || []).map((t, j) => (
                              <span key={j} className="inline-block mr-2">
                                {t.module_label} · {t.section_label || t.section_key} · <span className="text-zinc-700">{t.field_label || t.field_key}</span>
                              </span>
                            ))}
                          </div>
                        </>
                      ) : r.not_applicable_reason ? (
                        <span className="text-2xs text-zinc-500" data-testid={`pfc-na-reason-${r.template_id}-${r.field_id}`}>{r.not_applicable_reason}</span>
                      ) : <span className="italic text-zinc-400">— nowhere —</span>}
                    </td>
                  </tr>
                ))}
                {rows.length > 1000 && (
                  <tr><td className="px-2 py-1.5 italic text-zinc-500" colSpan={5}>
                    Showing first 1000 rows — refine the search box to narrow further.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
